// PainelAtencao — sidebar direita do dashboard com abas (Atenção / Próximas / Em campo / Todas)
// Itens com cores e ações rápidas.

import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { osLabel, osLabelByFields } from '../lib/visita.js';

function formatHora(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });
}

const STATUS_VISUAL_INFO = {
  concluida:    { label: 'concluída',  badge: 'done',    bg: '#dcfce7', fg: '#166534' },
  em_andamento_no_horario: { label: 'andamento',     badge: 'running', bg: '#dbeafe', fg: '#1e3a8a' },
  em_andamento_atrasada:   { label: 'and. atrasada', badge: 'warn',    bg: '#ffedd5', fg: '#7c2d12' },
  em_andamento: { label: 'andamento',  badge: 'running', bg: '#dbeafe', fg: '#1e3a8a' },
  proxima_2h:   { label: 'próxima',    badge: 'pending', bg: '#fef3c7', fg: '#92400e' },
  agendada:     { label: 'agendada',   badge: 'neutral', bg: '#f3f4f6', fg: '#4b5563' },
  atrasada:     { label: 'ATRASADA',   badge: 'late',    bg: '#fee2e2', fg: '#991b1b' },
  cancelada:    { label: 'cancelada',  badge: 'neutral', bg: '#f3f4f6', fg: '#9ca3af' },
};

export default function PainelAtencao({
  atencao = [],
  visitas = [],
  tecnicos = [],
  abaInicial = 'atencao',
  filtroExterno = null,        // se HealthBar mandar filtro, força a aba "Todas"
}) {
  const [aba, setAba] = useState(filtroExterno ? 'todas' : abaInicial);

  // Filtros derivados pra cada aba
  const proximas = useMemo(() => visitas.filter(v => v.status_visual === 'proxima_2h'), [visitas]);
  const emCampo = useMemo(() => visitas.filter(v =>
    v.status_visual === 'em_andamento_no_horario' ||
    v.status_visual === 'em_andamento_atrasada'
  ), [visitas]);
  const todasFiltradas = useMemo(() => {
    if (!filtroExterno) return visitas;
    return visitas.filter(v => v.status_visual === filtroExterno);
  }, [visitas, filtroExterno]);

  const items = aba === 'atencao' ? atencao
              : aba === 'proximas' ? proximas
              : aba === 'em-campo' ? emCampo
              : todasFiltradas;

  return (
    <div className="sidebar">
      <div className="tabs">
        <button className={`tab ${aba==='atencao'?'active danger':''}`} onClick={() => setAba('atencao')}>
          ⚠ Atenção <span className="count">{atencao.length}</span>
        </button>
        <button className={`tab ${aba==='proximas'?'active':''}`} onClick={() => setAba('proximas')}>
          Próximas <span className="count">{proximas.length}</span>
        </button>
        <button className={`tab ${aba==='em-campo'?'active':''}`} onClick={() => setAba('em-campo')}>
          Em campo <span className="count">{emCampo.length}</span>
        </button>
        <button className={`tab ${aba==='todas'?'active':''}`} onClick={() => setAba('todas')}>
          Todas <span className="count">{todasFiltradas.length}</span>
        </button>
      </div>

      <div className="tab-content">
        {items.length === 0 && (
          <div style={{ padding: '32px 16px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
            {aba === 'atencao' ? '✅ Tudo no prazo' :
             aba === 'proximas' ? 'Sem visitas próximas' :
             aba === 'em-campo' ? 'Sem visitas em andamento' :
             'Sem visitas'}
          </div>
        )}

        {/* Renderização dos itens da aba "atenção" (especial) */}
        {aba === 'atencao' && items.map(item => (
          <div key={`${item.tipo}-${item.visita_id}`} className={`os-card ${item.severidade === 'alta' ? 'late' : 'warn'}`}>
            <div className="os-head">
              <span className="os-id">{osLabelByFields(item.voalle_protocolo, item.visita_id)}</span>
              <span className={`os-tag ${item.tipo === 'atrasada' || item.tipo === 'sem_tecnico' ? 'late' : 'warn'}`}>
                {item.tipo === 'atrasada' && `ATRASADA ${item.minutos_atraso}min`}
                {item.tipo === 'sem_tecnico' && 'SEM TÉCNICO'}
                {item.tipo === 'tecnico_parado' && 'TÉCNICO PARADO'}
              </span>
            </div>
            <div className="os-cliente">{item.cliente_nome}</div>
            <div className="os-meta">
              {item.cliente_bairro && <span>{item.cliente_bairro}</span>}
              {item.tecnico_apelido && <> · <span>{item.tecnico_apelido}</span></>}
              {item.tipo_os && <> · <span style={{ fontSize: 10 }}>{item.tipo_os}</span></>}
            </div>
            <div className="os-alert">
              🚨 {item.motivo}
            </div>
            <div className="os-actions">
              <Link to={`/visitas/${item.visita_id}`} className="os-btn primary">Ver detalhe</Link>
            </div>
          </div>
        ))}

        {/* Renderização das demais abas (visita normal) */}
        {aba !== 'atencao' && items.map(v => {
          const info = STATUS_VISUAL_INFO[v.status_visual] || STATUS_VISUAL_INFO.agendada;
          return (
            <Link to={`/visitas/${v.id}`} key={v.id} className="os-card" style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
              <div className="os-head">
                <span className="os-id">{osLabel(v)} · {formatHora(v.janela_inicio)}-{formatHora(v.janela_fim)}</span>
                <span className="os-tag" style={{ background: info.bg, color: info.fg }}>{info.label}</span>
              </div>
              <div className="os-cliente">{v.cliente_nome || '—'}</div>
              <div className="os-meta">
                {v.cliente_bairro && <span>{v.cliente_bairro}</span>}
                {v.tecnico_apelido && <> · <span>{v.tecnico_apelido}</span></>}
                {!v.tecnico_apelido && <> · <span style={{ color: '#ef4444' }}>sem técnico</span></>}
                {v.tipo_os && <> · <span style={{ fontSize: 10 }}>{v.tipo_os}</span></>}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
